import React, { useState } from "react";
import { Box, TextField, CircularProgress, InputAdornment, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate } from "react-router-dom";
import LandingPage from "../../component/landingPage";
import BtnPrimary from "../../component/PrimaryButton";
import ErrorMsg from "../../component/ErrorMsg";
import { useNotify } from "../../component/NotificationProvider";
import { getUrlParameter } from "../../utils/getUrlParameter";
import { errorMessageFrom, fieldErrorsFrom } from "../../utils/formErrors";
import { activateAccount } from "../../services/firebase";
import { tokens, displayFont } from "../../theme";

// Firebase's own floor. Stated on the field rather than discovered on submit,
// and checked here so a too-short password never becomes a round trip that
// comes back as auth/weak-password.
const MIN_PASSWORD = 6;

// Public account-activation page reached from the employee invite email:
// /activate?userId=&verificationToken=&email=&organization=
// The employee sets a password → Firebase account created → linked to their
// pre-created User via the verification token. Then they log in.
const Activate = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const userId = getUrlParameter("userId");
  const verificationToken = getUrlParameter("verificationToken");
  const email = decodeURIComponent(getUrlParameter("email") || "");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Field-level first, form-level for what belongs to no single field — a
  // rejected token is about the link, not about anything they typed.
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  // Whether the invite itself is spent, which is not something a retry fixes.
  // The button becomes a way to ask for a new one instead of a way to try again.
  const [linkDead, setLinkDead] = useState(false);

  const invalidLink = !userId || !verificationToken || !email;

  // Same rules used to gate submit and to annotate the fields, so the button
  // and the message under the input can never disagree.
  const validate = () => {
    const errors = {};
    if (!password) errors.password = "Choose a password";
    else if (password.length < MIN_PASSWORD)
      errors.password = `Use at least ${MIN_PASSWORD} characters`;
    if (!confirm) errors.confirm = "Type the password again";
    else if (password && confirm !== password) errors.confirm = "This does not match the password above";
    return errors;
  };

  const submit = async () => {
    if (busy || linkDead) return;
    const errors = validate();
    setFieldErrors(errors);
    setFormError("");
    if (Object.keys(errors).length) return;

    setBusy(true);
    try {
      const res = await activateAccount({ email, password, userId, verificationToken });
      if (res?.success) {
        setDone(true);
        notify.success("Account activated");
        setTimeout(() => navigate("/login"), 1200);
        return;
      }

      if (res?.stage === "account") {
        // Firebase refused the credential itself — already mapped to a sentence
        // by activationAuthMessage. It is about the password, so it goes under
        // the password field.
        setFieldErrors({ password: res.message });
        setFormError(res.message);
        return;
      }

      // The API refused the link. A 400 here means the token is wrong, expired
      // or already spent, and no amount of retyping changes that.
      const serverFields = fieldErrorsFrom(res);
      const message = errorMessageFrom(res, "Could not activate your account");
      if (res?.status === 400 || res?.status === 409) setLinkDead(true);
      setFieldErrors(serverFields);
      setFormError(message);
      notify.error(message);
    } catch (e) {
      const message = e?.message || "Could not activate account";
      setFormError(message);
      notify.error(message);
    } finally {
      setBusy(false);
    }
  };

  // One toggle for both fields: they have to match, so revealing only one of
  // them tells you nothing about whether they do.
  const reveal = {
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          size="small"
          edge="end"
          aria-label={show ? "Hide password" : "Show password"}
          onClick={() => setShow((s) => !s)}
          onMouseDown={(e) => e.preventDefault()}
        >
          {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </InputAdornment>
    ),
  };

  const labelSx = { fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" };

  return (
    <LandingPage>
      <Box sx={{ width: "100%", maxWidth: "26rem", mx: "auto" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.75rem", fontWeight: 700, color: tokens.text, mb: "0.5rem" }}>
          Set up your account
        </Box>

        {invalidLink ? (
          <Box sx={{ fontSize: "0.875rem", color: tokens.danger, mt: "1rem" }}>
            This activation link is invalid or incomplete. Please use the link from your invite email,
            or ask your admin to resend the invite.
          </Box>
        ) : done ? (
          <Box sx={{ fontSize: "0.875rem", color: tokens.text, mt: "1rem" }}>
            Your account is ready. Redirecting you to sign in…
          </Box>
        ) : (
          <>
            <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mb: "1.5rem" }}>
              Create a password for <b style={{ color: tokens.text }}>{email}</b> to activate your account.
            </Box>

            <Box sx={labelSx}>New password</Box>
            <TextField fullWidth size="small" type={show ? "text" : "password"} value={password}
              autoComplete="new-password" InputProps={reveal} error={Boolean(fieldErrors.password)}
              disabled={busy || linkDead}
              placeholder={`At least ${MIN_PASSWORD} characters`}
              onChange={(e) => {
                setPassword(e.target.value);
                // Clear on edit rather than re-validating on every keystroke:
                // telling somebody their password is too short while they are
                // still typing it is noise, not help.
                if (fieldErrors.password) setFieldErrors((f) => ({ ...f, password: "" }));
              }}
              sx={{ mb: fieldErrors.password ? 0 : "1rem" }} />
            <ErrorMsg>{fieldErrors.password}</ErrorMsg>
            {fieldErrors.password ? <Box sx={{ mb: "1rem" }} /> : null}

            <Box sx={labelSx}>Confirm password</Box>
            <TextField fullWidth size="small" type={show ? "text" : "password"} value={confirm}
              autoComplete="new-password" InputProps={reveal} error={Boolean(fieldErrors.confirm)}
              disabled={busy || linkDead}
              onChange={(e) => {
                setConfirm(e.target.value);
                if (fieldErrors.confirm) setFieldErrors((f) => ({ ...f, confirm: "" }));
              }}
              onBlur={() => {
                // Checked on blur, not while typing: a mismatch is only
                // meaningful once they have finished the second field.
                if (confirm && password && confirm !== password)
                  setFieldErrors((f) => ({ ...f, confirm: "This does not match the password above" }));
              }}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              sx={{ mb: fieldErrors.confirm ? 0 : "1.5rem" }} />
            <ErrorMsg>{fieldErrors.confirm}</ErrorMsg>
            {fieldErrors.confirm ? <Box sx={{ mb: "1.5rem" }} /> : null}

            {/* Only when it says something the field annotations do not. */}
            {formError && !fieldErrors.password && !fieldErrors.confirm ? (
              <Box sx={{ mb: "1rem" }}><ErrorMsg>{formError}</ErrorMsg></Box>
            ) : null}

            {linkDead ? (
              <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mb: "1.5rem" }}>
                Activation links expire after seven days and can only be used once. Ask your admin to
                resend the invite, then open the newest email.
              </Box>
            ) : null}

            <BtnPrimary onClick={submit} disabled={busy || linkDead}
              sx={{ width: "100%", height: "3rem", borderRadius: "10px" }}>
              {busy ? <CircularProgress size={"1.25rem"} sx={{ color: "#fff" }} /> : "Activate account"}
            </BtnPrimary>
          </>
        )}
      </Box>
    </LandingPage>
  );
};

export default Activate;
